import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import AppLayout from '~/components/appLayout'
import { toastError, toastInfo, toastSuccess } from '~/components/toast'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import OptionsMenu from '~/packages/option/menu.component'
import getMonitorUserPermissionsDefinition from './getMonitorUserPermissions.definition'
import MonitorUserPermissionsSettings, {
  MonitorUserPermissionViewItem,
} from './monitorUserPermissionsSettings.component'
import updateMonitorUserPermissionsDefinition from './updateMonitorUserPermissions.definition'

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 22px;
`

const buildPermissionStateMap = (items: MonitorUserPermissionViewItem[]) =>
  items.reduce<Record<string, boolean>>((currentPermissionStates, permission) => {
    currentPermissionStates[permission.key] = permission.isEnabled
    return currentPermissionStates
  }, {})

const getSelectedPermissionKeys = (permissionStates: Record<string, boolean>) =>
  Object.entries(permissionStates)
    .filter(([, isEnabled]) => isEnabled)
    .map(([permissionKey]) => permissionKey)

export default function MonitorUserPermissionsScreen() {
  const [permissions, setPermissions] = useState<MonitorUserPermissionViewItem[]>([])
  const [permissionStates, setPermissionStates] = useState<Record<string, boolean>>({})
  const [savedPermissionStates, setSavedPermissionStates] = useState<Record<string, boolean>>({})
  const [receiveWeeklyInsightsEmail, setReceiveWeeklyInsightsEmail] = useState(false)
  const [savedReceiveWeeklyInsightsEmail, setSavedReceiveWeeklyInsightsEmail] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>()

  const {
    callAction: getMonitorUserPermissions,
    isLoading: isLoadingPermissions,
    error: loadPermissionsError,
  } = useActionFunction(getMonitorUserPermissionsDefinition, {
    initialIsLoading: false,
  })
  const { callAction: updateMonitorUserPermissions } = useActionFunction(
    updateMonitorUserPermissionsDefinition,
    {
      initialIsLoading: false,
    },
  )

  const loadPermissions = useCallback(async () => {
    setErrorMessage(undefined)

    try {
      const result = await getMonitorUserPermissions({}).resultPromise

      setPermissions(result.items)
      setIsAdmin(result.isAdmin)
      setReceiveWeeklyInsightsEmail(result.receiveWeeklyInsightsEmail)
      setSavedReceiveWeeklyInsightsEmail(result.receiveWeeklyInsightsEmail)

      const initialPermissionStates = buildPermissionStateMap(result.items)
      setPermissionStates(initialPermissionStates)
      setSavedPermissionStates(initialPermissionStates)
    } catch (error) {
      console.error(error)
      setErrorMessage('Não foi possível carregar seus tópicos de interesse.')
    }
  }, [getMonitorUserPermissions])

  useEffect(() => {
    loadPermissions().catch(error => console.error(error))
  }, [loadPermissions])

  useEffect(() => {
    if (!loadPermissionsError) {
      return
    }

    toastError('Erro ao carregar tópicos de interesse.')
  }, [loadPermissionsError])

  const hasChanges = useMemo(
    () =>
      JSON.stringify(permissionStates) !== JSON.stringify(savedPermissionStates) ||
      receiveWeeklyInsightsEmail !== savedReceiveWeeklyInsightsEmail,
    [
      permissionStates,
      receiveWeeklyInsightsEmail,
      savedPermissionStates,
      savedReceiveWeeklyInsightsEmail,
    ],
  )
  const allPermissionsEnabled = useMemo(
    () =>
      permissions.length > 0 &&
      permissions.every(permission => (permissionStates[permission.key] ?? false) === true),
    [permissionStates, permissions],
  )

  const handleToggleAllPermissions = useCallback(() => {
    const nextValue = !allPermissionsEnabled

    setPermissionStates(
      permissions.reduce<Record<string, boolean>>((nextPermissionStates, permission) => {
        nextPermissionStates[permission.key] = nextValue
        return nextPermissionStates
      }, {}),
    )
  }, [allPermissionsEnabled, permissions])

  const handleTogglePermission = useCallback((permissionKey: string) => {
    setPermissionStates(currentPermissionStates => ({
      ...currentPermissionStates,
      [permissionKey]: !currentPermissionStates[permissionKey],
    }))
  }, [])

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      if (!hasChanges) {
        return
      }

      setIsSaving(true)

      try {
        toastInfo('Salvando as alterações...')

        await updateMonitorUserPermissions({
          permissionKeys: getSelectedPermissionKeys(permissionStates),
          receiveWeeklyInsightsEmail,
        }).resultPromise

        await loadPermissions()
        toastSuccess('Tópicos salvos com sucesso!')
      } catch (error) {
        console.error(error)
        toastError('Erro ao salvar tópicos de interesse.')
      } finally {
        setIsSaving(false)
      }
    },
    [
      hasChanges,
      loadPermissions,
      permissionStates,
      receiveWeeklyInsightsEmail,
      updateMonitorUserPermissions,
    ],
  )

  return (
    <AppLayout
      title='Configurações de Tópicos de Interesse'
      dockActive='options'
      menu={<OptionsMenu menuActive='monitor-permissions' />}
      card={{
        title: 'Configurações de Tópicos de Interesse',
      }}
    >
      <Wrapper>
        <MonitorUserPermissionsSettings
          permissions={permissions}
          permissionStates={permissionStates}
          isLoading={isLoadingPermissions}
          isSaving={isSaving}
          isAdmin={isAdmin}
          receiveWeeklyInsightsEmail={receiveWeeklyInsightsEmail}
          hasChanges={hasChanges}
          allPermissionsEnabled={allPermissionsEnabled}
          errorMessage={errorMessage}
          onRetry={() => loadPermissions().catch(error => console.error(error))}
          onToggleAllPermissions={handleToggleAllPermissions}
          onTogglePermission={handleTogglePermission}
          onToggleReceiveWeeklyInsightsEmail={() =>
            setReceiveWeeklyInsightsEmail(currentValue => !currentValue)
          }
          onSubmit={handleSubmit}
        />
      </Wrapper>
    </AppLayout>
  )
}
